import { Button, Group, Loader, Paper, Text } from '@mantine/core';
import { useState } from 'react';

import {
  type ArenaRun,
  type ReportError,
  useArenaCapability,
  useArenaRunsInFlight,
  useCancelArenaRun,
} from '../hooks/useModelArena';
import { progress, runMoment } from '../runGrouping';
import { elapsedLabel, shortDateTimeLabel } from '../runLabels';

import { type Column, DataTable, type Row } from './DataTable';
import { sentenceCase } from './metricFormat';
import { StatusBadge } from './StatusBadge';

const when = (run: ArenaRun) =>
  `${run.startedAt === null ? 'due' : 'started'} ${shortDateTimeLabel(runMoment(run))}`;

/** Candidates finished out of candidates asked for. Measured, because each is saved as it lands —
 * but it moves a whole candidate at a time, which is tens of minutes. */
const progressLabel = (run: ArenaRun): string | null => {
  if (run.status !== 'RUNNING') return null;

  const { measured, total } = progress(run);
  const elapsed = elapsedLabel(run, new Date());
  return `${measured} of ${total}${elapsed ? ` · ${elapsed}` : ''}`;
};

/**
 * Everything queued or running, across every task.
 *
 * Read from its own endpoint and shown above the tasks, so it answers "what is happening right now"
 * for anyone who opens the page — not only for whoever queued the run, and not only for the tasks
 * they happen to have expanded.
 */
export function InFlightTable({ reportError }: { reportError: ReportError }) {
  const { inFlight } = useArenaRunsInFlight();
  const { capability } = useArenaCapability();
  const { cancelRun, isCancelling } = useCancelArenaRun(reportError);
  // Which run, not whether any: one flag shared by every row disables them all while one settles,
  // and a click on another in that window is swallowed by a button that looks busy.
  const [cancelling, setCancelling] = useState<string | null>(null);

  if (inFlight.length === 0) return null;

  const stop = async (runId: string) => {
    setCancelling(runId);
    await cancelRun(runId);
    setCancelling(null);
  };

  const byId = Object.fromEntries(inFlight.map(run => [run.id, run]));

  const columns: Column[] = [
    { key: 'task', label: 'Task' },
    { key: 'candidates', label: 'Candidates' },
    { key: 'when', label: 'When' },
    { key: 'status', label: 'Status', render: row => <StatusCell run={byId[String(row.id)]} /> },
    { key: 'progress', label: 'Progress' },
    {
      key: 'cancel',
      label: '',
      render: row => (
        <Group justify="flex-end">
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={() => stop(String(row.id))}
            loading={isCancelling && cancelling === String(row.id)}
            // The same source of truth the API refuses on, so the button is absent where it could
            // only ever return 409 rather than live and failing on click.
            disabled={capability?.mayRun === false}
          >
            Cancel
          </Button>
        </Group>
      ),
    },
  ];

  const rows: Row[] = inFlight.map(run => ({
    id: run.id,
    task: sentenceCase(run.taskKey),
    candidates: run.candidates.join(', '),
    when: when(run),
    status: run.status.toLowerCase(),
    progress: progressLabel(run),
  }));

  return (
    <Paper withBorder p="sm" radius="sm">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
        In flight
      </Text>
      <DataTable columns={columns} rows={rows} minWidth={720} />
    </Paper>
  );
}

function StatusCell({ run }: { run: ArenaRun }) {
  return (
    <Group gap={6} wrap="nowrap">
      {run.status === 'RUNNING' && <Loader size={12} />}
      <StatusBadge status={run.status} />
    </Group>
  );
}
